import { useTracking } from '@vp/react-tracking';
import React, { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Card, FlexBox, Typography, responsive } from '@vp/swan';
import _ from 'lodash';
import styled from 'styled-components';
import { __, getCurrentCulture } from '@99designs/i18n';
import { Project } from '../utils/project.transformer';
import { useIsBifrostEnabled } from './RegionLockedConfiguration';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const StyledAvatar = styled.a`
    border-radius: 50%;
    display: inline-block;
    vertical-align: middle;
    position: relative;
`;

const StyledImage = styled.img`
    border-radius: 50%;
    display: block;
    width: 32px;
    height: 32px;
`;

const ResponsiveFlexBox = responsive(FlexBox);

const ProjectsRequestCard: React.FC<Project> = ({
    id,
    title,
    previewUrl,
    description,
    createdDate,
    designer,
    categoryTitle,
    projectUrl,
    analytics,
    jobId,
}: Project) => {
    const { ref, inView } = useInView();
    const { tracking } = useTracking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const sendEventOnce = useCallback(_.once(tracking.track), []);
    const currentCulture = getCurrentCulture();
    const isMatchedProjectsEnabled = useIsBifrostEnabled(currentCulture);
    const isMatchedOnVistaprint = !!jobId && isMatchedProjectsEnabled;

    useEffect(() => {
        if (inView) {
            sendEventOnce('Project Viewed', {
                ...analytics,
                action: 'Project Viewed',
                eventDetail: `${id}`,
            });
        }
    }, [id, inView, analytics, sendEventOnce]);

    projectUrl = isMatchedOnVistaprint ? `/design/matching/${jobId}` : projectUrl;
    description = isMatchedOnVistaprint
        ? __(
              'Designers whose skills and preferences are a good fit for your project will now review your brief and let you know they are interested.'
          )
        : description;

    const ctas: CTA[] = [
        {
            label: isMatchedOnVistaprint ? __('Select designer') : __('Open Project'),
            iconType: isMatchedOnVistaprint ? 'designerMarketplace' : 'workWithAPro',
            href: projectUrl,
            onClick: () => {
                tracking.track('Navigation Clicked', {
                    ...analytics,
                    action: 'Navigation Clicked',
                    navigationDetail: 'open-project',
                });
            },
            openInNewTab: !isMatchedOnVistaprint,
            skin: 'primary',
        },
    ];

    return (
        <div ref={ref}>
            <Card bordered>
                <ResponsiveFlexBox
                    xs={{
                        flexDirection: 'column',
                        gap: 'between-subsections',
                        alignItems: 'center',
                    }}
                    sm={{
                        flexDirection: 'row',
                        gap: 'between-sections',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Project image thumbnail */}
                    <RequestCardImage imgSrc={previewUrl} imgAlt={__('Design Preview')} />

                    {/* Project details */}
                    <ResponsiveFlexBox
                        style={{ flex: '1 1 100%' }}
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-subsections',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <ResponsiveFlexBox
                            xs={{
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 'between-actions',
                            }}
                            sm={{ alignItems: 'flex-start' }}
                        >
                            <RequestCardSummary
                                title={title}
                                linkUrl={projectUrl}
                                createdAt={createdDate}
                                productName={categoryTitle}
                                analytics={analytics}
                            />
                            <Typography fontSkin="body-standard">{description}</Typography>
                        </ResponsiveFlexBox>
                        <ResponsiveFlexBox
                            xs={{ flexDirection: 'column', alignItems: 'center', gap: '3' }}
                            sm={{ alignItems: 'flex-start' }}
                        >
                            {designer && (
                                <FlexBox alignItems="center" gap="3">
                                    <StyledAvatar
                                        href={designer.profileUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() =>
                                            tracking.track('Navigation Clicked', {
                                                ...designer.analytics,
                                                action: 'Navigation Clicked',
                                                navigationDetail: 'designer-avatar',
                                            })
                                        }
                                    >
                                        <StyledImage alt={__('avatar')} src={designer.avatarUrl} />
                                    </StyledAvatar>
                                    <div>
                                        <Typography fontSkin="body-small" display="inline">
                                            {__('with')}{' '}
                                        </Typography>
                                        <a
                                            href={designer.profileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title={__('link to designers profile')}
                                            onClick={() =>
                                                tracking.track('Navigation Clicked', {
                                                    ...designer.analytics,
                                                    action: 'Navigation Clicked',
                                                    navigationDetail: 'designer-profile',
                                                })
                                            }
                                        >
                                            <Typography fontSkin="body-small">
                                                {designer.displayName}
                                            </Typography>
                                        </a>
                                    </div>
                                </FlexBox>
                            )}
                            <img
                                src="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/99designsLogo_2x.png"
                                alt={__('99designs by vista logo')}
                                style={{ width: '74px', height: '25px' }}
                            />
                        </ResponsiveFlexBox>
                    </ResponsiveFlexBox>
                    {/* Project CTA */}
                    <RequestCardCTA ctas={ctas} />
                </ResponsiveFlexBox>
            </Card>
        </div>
    );
};

export { ProjectsRequestCard };
